
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { 
  Mail, 
  ArrowRight,
  ArrowLeft,
  Shield,
  Clock,
  CheckCircle,
  Heart,
  Sparkles,
  RefreshCw
} from 'lucide-react';

interface ForgotPasswordForm {      
    email: string;
}

export const ForgotPasswordPage: React.FC = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isFormFocused, setIsFormFocused] = useState(false);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');

  const { 
    register, 
    handleSubmit, 
    formState: { errors, isValid } 
  } = useForm<ForgotPasswordForm>({
    mode: 'onChange',
    defaultValues: {
      email: "test@gful.dev",
    },
  });
  
    
  const validateEmail = (email: string) => {
    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
    if (!email) {
      return "Email is required";
    }
    if (!emailRegex.test(email)) {
      return "Invalid email address";
    }
    return '';
  };
  
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    setEmailError(validateEmail(value));
  };
  
  const onSubmit = async (data: ForgotPasswordForm) => {
    const error = validateEmail(email);
    if (error) {
      setEmailError(error);
      return;
    }
    
    setLoading(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 2000));
    setLoading(false);
    setIsSubmitted(true);
  };

  const features = [
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Secure Reset Process",
      description: "Your security is our priority with encrypted reset links"
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: "Quick Recovery",
      description: "Get back to your practice in minutes, not hours"
    },
    {
      icon: <CheckCircle className="w-6 h-6" />,
      title: "Verified Access",
      description: "Multi-step verification ensures account protection"
    }
  ];

  const handleResendEmail = async () => {
    setLoading(true);
    // Simulate resend API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    setLoading(false);
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center p-4">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-6xl grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12"
      >
        
        {/* Welcome Section */}
        <motion.div 
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="flex flex-col justify-center items-start bg-gradient-to-br from-indigo-600 via-purple-600 to-indigo-700 text-white p-8 md:p-12 rounded-2xl shadow-2xl relative overflow-hidden"
        >
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-10 left-10 w-20 h-20 bg-white rounded-full blur-3xl"></div>
            <div className="absolute bottom-10 right-10 w-32 h-32 bg-purple-300 rounded-full blur-3xl"></div>
            <div className="absolute top-1/2 left-1/3 w-16 h-16 bg-indigo-300 rounded-full blur-2xl"></div>
          </div>
          
          <div className="relative z-10">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6"
            >
              <Sparkles className="w-4 h-4 text-yellow-300" />
              <span className="text-sm font-medium">Secure & Reliable</span>
            </motion.div>
            
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="text-3xl md:text-5xl font-bold mb-4 leading-tight"
            >
              {isSubmitted ? "Check Your Email" : "Reset Your Password"}
            </motion.h1>
            
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
              className="text-lg md:text-xl mb-8 text-indigo-100 leading-relaxed"
            >
              {isSubmitted 
                ? "We've sent password reset instructions to your email address. Follow the link to create a new password."
                : "No worries! Enter your email address and we'll send you instructions to reset your password."
              }
            </motion.p>
            
            {/* Features List */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 1 }}
              className="space-y-4 mb-8"
            >
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 1.2 + index * 0.1 }}
                  className="flex items-start gap-3"
                >
                  <div className="flex-shrink-0 p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                    {feature.icon}
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-1">{feature.title}</h3>
                    <p className="text-sm text-indigo-100">{feature.description}</p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
            
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 1.5 }}
              className="text-indigo-100"
            >
              Remember your password?{" "}
              <a 
                href="/login" 
                className="font-bold text-yellow-300 hover:text-yellow-200 transition-colors duration-200 underline underline-offset-2"
              >
                Sign in here
              </a>
            </motion.p>
          </div>
        </motion.div>
        
        {/* Form Section */}
        <motion.div 
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="flex flex-col justify-center"
        >
          <motion.div 
            animate={{
              scale: isFormFocused ? 1.02 : 1,
              boxShadow: isFormFocused 
                ? "0 25px 50px -12px rgba(0, 0, 0, 0.15)" 
                : "0 10px 25px -5px rgba(0, 0, 0, 0.1)"
            }}
            transition={{ duration: 0.3 }}
            className="bg-white p-8 md:p-10 shadow-xl rounded-2xl border border-gray-100"
            onFocus={() => setIsFormFocused(true)}
            onBlur={() => setIsFormFocused(false)}
          >
            <AnimatePresence mode="wait">
              {!isSubmitted ? (
                <motion.div
                  key="form"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.5 }}
                >
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.6 }}
                    className="text-center mb-8"
                  >
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl mb-4 shadow-lg">
                      <Heart className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                      Forgot Password?
                    </h2>
                    <p className="text-gray-600">
                      Enter your email to receive reset instructions
                    </p>
                  </motion.div>
                  
                  <div className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
                    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: 0.8 }}
                    >
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 z-10" />
                        <input
                          id="email"
                          type="email"
                          value={email}
                          onChange={handleEmailChange}
                          placeholder="Enter your email address"
                          className={`pl-12 pr-4 py-4 w-full text-base rounded-xl bg-gray-50 border-2 transition-all duration-200 ${
                            emailError
                              ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
                              : "border-gray-200 focus:border-indigo-500 focus:ring-indigo-500/20 hover:border-gray-300"
                          } placeholder-gray-400 focus:outline-none focus:ring-4`}
                        />
                      </div>
                      {emailError && (
                        <motion.p
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="mt-2 text-sm text-red-600"
                        >
                          {emailError}
                        </motion.p>
                      )}
                    </motion.div>
                    
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: 0.9 }}
                    >
                      <motion.button
                        type="button"
                        onClick={handleSubmit(onSubmit) as any}
                        disabled={loading || !email || emailError}
                        whileHover={{ scale: loading ? 1 : 1.02 }}
                        whileTap={{ scale: loading ? 1 : 0.98 }}
                        className={`group w-full flex items-center justify-center py-4 px-6 border border-transparent rounded-xl text-base font-semibold text-white transition-all duration-200 ${
                          loading || !email || emailError
                            ? "bg-gray-400 cursor-not-allowed"
                            : "bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 shadow-lg hover:shadow-xl"
                        } focus:outline-none focus:ring-4 focus:ring-indigo-500/20`}
                      >
                        {loading ? (
                          <div className="flex items-center">
                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                            Sending Reset Link...
                          </div>
                        ) : (
                          <div className="flex items-center">
                            Send Reset Link
                            <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform duration-200" />
                          </div>
                        )}
                      </motion.button>
                    </motion.div>
                    </form>
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.5 }}
                  className="text-center"
                >
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                    className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl mb-6 shadow-lg"
                  >
                    <CheckCircle className="w-8 h-8 text-white" />
                  </motion.div>
                  
                  <motion.h2
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.4 }}
                    className="text-2xl md:text-3xl font-bold text-gray-900 mb-4"
                  >
                    Email Sent!
                  </motion.h2>
                  
                  <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.6 }}
                    className="text-gray-600 mb-6 leading-relaxed"
                  >
                    We've sent password reset instructions to{" "}
                    <span className="font-semibold text-gray-900">{email}</span>
                    <br />
                    Please check your email and follow the link to reset your password.
                  </motion.p>
                  
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.8 }}
                    className="space-y-4"
                  >
                    <p className="text-sm text-gray-500">
                      Didn't receive the email? Check your spam folder or
                    </p>
                    
                    <motion.button
                      onClick={handleResendEmail}
                      disabled={loading}
                      whileHover={{ scale: loading ? 1 : 1.02 }}
                      whileTap={{ scale: loading ? 1 : 0.98 }}
                      className={`inline-flex items-center justify-center px-6 py-3 border border-indigo-600 rounded-xl text-base font-medium text-indigo-600 transition-all duration-200 ${
                        loading
                          ? "cursor-not-allowed opacity-50"
                          : "hover:bg-indigo-50 hover:shadow-md"
                      } focus:outline-none focus:ring-4 focus:ring-indigo-500/20`}
                    >
                      {loading ? (
                        <div className="flex items-center">
                          <div className="w-4 h-4 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin mr-2"></div>
                          Resending...
                        </div>
                      ) : (
                        <div className="flex items-center">
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Resend Email
                        </div>
                      )}
                    </motion.button>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
            
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 1.2 }}
              className="mt-8 pt-6 border-t border-gray-100 text-center"
            >
              <Link 
                to="/login" 
                className="inline-flex items-center text-indigo-600 hover:text-indigo-500 font-medium transition-colors duration-200"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Sign In
              </Link>
            </motion.div>
          </motion.div>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default ForgotPasswordPage;